import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getSales, getEquipment, createSale, deleteSale } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, ShoppingCart, Trash2 } from "lucide-react";

export default function Sales() {
  const { canDelete } = useAuth();
  const [sales, setSales] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    equipment_id: "",
    sale_date: "",
    sale_price: "",
    buyer_name: "",
    buyer_contact: "",
    notes: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [salesRes, equipmentRes] = await Promise.all([
        getSales(),
        getEquipment()
      ]);
      setSales(salesRes.data);
      setEquipment(equipmentRes.data.filter(e => e.status === "Verfügbar"));
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      equipment_id: "",
      sale_date: "",
      sale_price: "",
      buyer_name: "",
      buyer_contact: "",
      notes: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createSale({
        ...formData,
        sale_price: parseFloat(formData.sale_price)
      });
      toast.success("Verkauf erfasst");
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm(`Möchten Sie den Verkauf von "${sale.equipment_name}" wirklich löschen?`)) return;
    try {
      await deleteSale(sale.id);
      toast.success("Verkauf gelöscht");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE");
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value, currency = "TND") => {
    if (currency === "TND") {
      return `${(value || 0).toFixed(3)} TND`;
    }
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(value || 0);
  };

  const filteredSales = sales.filter((sale) => {
    return (
      sale.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.equipment_serial?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalRevenue = sales.reduce((sum, sale) => sum + (sale.sale_price || 0), 0);

  return (
    <div data-testid="sales-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Verkäufe</h1>
          <p className="page-subtitle">Erfassen Sie Equipment-Verkäufe</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="new-sale-btn">
              <Plus className="w-4 h-4" />
              Verkauf erfassen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="sale-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Neuer Verkauf
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">Equipment</Label>
                <Select
                  value={formData.equipment_id}
                  onValueChange={(value) => setFormData({ ...formData, equipment_id: value })}
                  required
                >
                  <SelectTrigger data-testid="sale-equipment-select">
                    <SelectValue placeholder="Equipment wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipment.length === 0 ? (
                      <SelectItem value="none" disabled>
                        Kein Equipment verfügbar
                      </SelectItem>
                    ) : (
                      equipment.map((eq) => (
                        <SelectItem key={eq.id} value={eq.id}>
                          {eq.name} ({eq.serial_number})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Verkaufsdatum</Label>
                  <Input
                    type="date"
                    value={formData.sale_date}
                    onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
                    required
                    data-testid="sale-date-input"
                  />
                </div>
                <div>
                  <Label className="form-label">Verkaufspreis (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="sale-price-input"
                  />
                </div>
              </div>
              <div>
                <Label className="form-label">Käufer Name</Label>
                <Input
                  value={formData.buyer_name}
                  onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
                  placeholder="Name des Käufers"
                  required
                  data-testid="sale-buyer-input"
                />
              </div>
              <div>
                <Label className="form-label">Kontakt (optional)</Label>
                <Input
                  value={formData.buyer_contact}
                  onChange={(e) => setFormData({ ...formData, buyer_contact: e.target.value })}
                  placeholder="E-Mail oder Telefon"
                  data-testid="sale-contact-input"
                />
              </div>
              <div>
                <Label className="form-label">Notizen (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Zusätzliche Informationen zum Verkauf..."
                  rows={3}
                  data-testid="sale-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" data-testid="sale-submit-btn">
                  Verkauf speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Card */}
      <div className="mb-6">
        <div className="metric-card inline-flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-green-600" strokeWidth={1.5} />
          </div>
          <div>
            <p className="metric-card-label">Gesamtumsatz</p>
            <p className="metric-card-value text-green-600">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Suchen nach Equipment oder Käufer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="sale-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold">Equipment</TableHead>
              <TableHead className="font-semibold">Seriennummer</TableHead>
              <TableHead className="font-semibold">Käufer</TableHead>
              <TableHead className="font-semibold">Verkaufsdatum</TableHead>
              <TableHead className="font-semibold text-right">Preis</TableHead>
              {canDelete() && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canDelete() ? 6 : 5} className="text-center py-8 text-slate-500">
                  Lädt...
                </TableCell>
              </TableRow>
            ) : filteredSales.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canDelete() ? 6 : 5} className="text-center py-8 text-slate-500">
                  Keine Verkäufe gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredSales.map((sale) => (
                <TableRow key={sale.id} className="table-row-hover" data-testid={`sale-row-${sale.id}`}>
                  <TableCell className="font-medium">{sale.equipment_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">
                    {sale.equipment_serial}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{sale.buyer_name}</p>
                      {sale.buyer_contact && (
                        <p className="text-sm text-slate-500">{sale.buyer_contact}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(sale.sale_date)}</TableCell>
                  <TableCell className="text-right font-semibold text-green-600">
                    {formatCurrency(sale.sale_price)}
                  </TableCell>
                  {canDelete() && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(sale)}
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Verkauf löschen"
                        data-testid={`delete-sale-btn-${sale.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
